import React, { useState, useEffect, useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'

import { useHotKeys } from '~/shared/hooks'
import { useCurrentPost, useCurrentChannel } from '~/app/state/hooks'
import PostView from './Post'
import DismissWarning from './DismissWarning'
import DismissAllWarning from './DismissAllWarning'
import { QUERY_POST } from '~/modules/post/queries'
import { useAccountContext } from '~/modules/account/context'
import type { Comment } from '~/gql/graphql.ts'

import {
  useDismissAll,
  useCommentProcessing,
  useCommentSelection,
} from '~/modules/post/hooks'
import type { Channel } from '~/modules/account/types'

type Account = {
  currentOrganization?: {
    channels: Channel[]
  }
} | null

export type PostProps = {
  postId: string
  disabled?: boolean
  error?: boolean | null
  showDismissWarning?: boolean
  onDismissWarningSubmit: (showAgainChecked: boolean) => void
}

type cb = () => void

/**
 *
 */
const Post = ({
  postId,
  disabled,
  error,
  showDismissWarning,
  onDismissWarningSubmit,
}: PostProps): JSX.Element => {
  const [dialogOpened, setDialogOpened] = useState<string | null>(null)
  const [dialogCallback, setDialogCallback] = useState<cb | null>(null)

  const { processComment, unprocessComment } = useCommentProcessing(postId)
  const { dismissAll } = useDismissAll()

  const { currentPost, queryCurrentPost, mutateCurrentPost } = useCurrentPost()

  const [queryPost, { data, error: queryError, loading: queryLoading }] =
    useLazyQuery(QUERY_POST, {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    })

  const post = data ? data.engagePost : null

  const { selectedComment, actions } = useCommentSelection()

  const { selectComment, selectPrev, selectNext } = actions

  const { comments = [] } = post || {}

  useEffect(() => {
    queryCurrentPost()
  }, [])

  useEffect(() => {
    if (!postId) {
      return
    }

    selectComment(null)

    queryPost({
      fetchPolicy: 'network-only',
      variables: { id: postId, refreshMedia: false },
    })
  }, [postId])

  useEffect(() => {
    if (!post || selectedComment) {
      return
    }

    selectNext(post.comments)
  }, [post])

  const onDismissComment = useCallback(
    (comment: Comment | null = null, usedHotKey = false) => {
      const dismiss = (comment: Comment | null, usedHotKey: boolean): void => {
        selectNext(comments, comment)
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ comment: any; tracking: { used... Remove this comment to see the full error message
        processComment({ comment, tracking: { usedHotKey } })
      }

      if (showDismissWarning) {
        setDialogOpened('dismiss')

        setDialogCallback(() => (showAgainChecked: boolean) => {
          dismiss(comment, usedHotKey)
          onDismissWarningSubmit(showAgainChecked)
          setDialogOpened(null)
        })
      } else {
        dismiss(comment, usedHotKey)
      }
    },
    [
      showDismissWarning,
      selectNext,
      comments,
      processComment,
      onDismissWarningSubmit,
    ],
  )

  const onDismissSelectedComment = useCallback(() => {
    if (selectedComment) {
      onDismissComment(selectedComment, true)
    }
  }, [selectedComment, onDismissComment])

  /**
   * We have to set up a global event listener for the Ctrl+D hotkey to manage
   * the default behavior of the browser.
   */
  useEffect(() => {
    if (!selectedComment) {
      return
    }

    const handleCtrlD = (e: KeyboardEvent): void => {
      if (e.ctrlKey && e.key.toLowerCase() === 'd') {
        e.preventDefault() // Prevent default browser behavior
        e.stopPropagation()
        onDismissSelectedComment()
      }
    }
    window.addEventListener('keydown', handleCtrlD)

    return (): void => {
      if (selectedComment) {
        window.removeEventListener('keydown', handleCtrlD)
      }
    }
  }, [onDismissSelectedComment, selectedComment])

  const onSelectComment = (selected: any): void => {
    selectComment(selected, { post })
  }

  const onAddReply = useCallback(
    (comment: any, text = '', tracking = {}) => {
      if (!text) {
        return
      }
      selectNext(comments, comment)
      processComment({ comment, text, tracking })
    },
    [post, comments],
  )

  const onUnDismissComment = useCallback(
    (comment: any) => {
      unprocessComment({ comment })
      selectComment(comment)
    },
    [unprocessComment, selectComment],
  )

  const onGoToComment = (direction = 'up'): void => {
    const trackingData = { post, usedHotKey: true }
    const handler = direction === 'up' ? selectPrev : selectNext
    handler(comments, selectedComment, trackingData)
  }

  const onDismissAll = (): void => {
    setDialogOpened('dismissAll')

    setDialogCallback(() => () => {
      dismissAll(postId)
      setDialogOpened(null)
    })
  }

  const onCloseDialog = (): void => {
    setDialogOpened(null)
  }

  const onEngageCompleted = (): void => {
    mutateCurrentPost({ engageCompleted: true })
  }

  // Hotkeys.
  useHotKeys(
    [
      // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
      { keys: 'up', handler: () => onGoToComment('up') },
      // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
      { keys: 'down', handler: () => onGoToComment('down') },
    ],
    [onGoToComment],
  )

  const Dialog = useCallback(
    dialogOpened === 'dismiss' ? DismissWarning : DismissAllWarning,
    [dialogOpened],
  )

  const loading = (!postId && !error) || queryLoading
  const loadingError = !!queryError

  const { getCurrentChannel } = useCurrentChannel()
  const currentChannel = getCurrentChannel()
  const account: Account = useAccountContext()

  return (
    <>
      <PostView
        post={postId ? post : null}
        fallbackImg={currentPost?.fallbackImg}
        placeholder={loading}
        error={loadingError}
        disabled={disabled}
        selectedComment={selectedComment}
        onSelectComment={onSelectComment}
        onDismissComment={onDismissComment}
        onUnDismissComment={onUnDismissComment}
        onDismissAll={onDismissAll}
        onAddReply={onAddReply}
        onEngageCompleted={onEngageCompleted}
        currentChannel={currentChannel}
        accountChannels={account?.currentOrganization?.channels || []}
      />
      {dialogOpened && (
        // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type '(...args: a... Remove this comment to see the full error message
        <Dialog onSubmit={dialogCallback} onCancel={onCloseDialog} />
      )}
    </>
  )
}

export default Post
